import { useState, useEffect } from "react";
import {
    Grid,
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    useObtenerRangosComision,
    useCrearRangoComision,
    useObtenerTiposComision,
    useObtenerTiposAsignadosDistribuidores,
    useAsignarTipoDistribuidor,
    useObtenerGruposCargosAsignados,
    useObtenerEmpleadosIdCargo,
    RangoComision,
    TipoComision,
    DistribuidorTipoAsignado,
    GrupoVendedor,
} from "@/shared/actions/erp/reporte-comisiones-vendedores.actions";

type AdminView = "menu" | "comisiones" | "grupos" | "asignarTipo";

export default function AdminPanel() {
    const [activeView, setActiveView] = useState<AdminView>("menu");

    // Estados para tabla de rangos
    const [rangos, setRangos] = useState<RangoComision[]>([]);
    const [isLoadingRangos, setIsLoadingRangos] = useState(false);
    const [errorRangos, setErrorRangos] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [filterText, setFilterText] = useState("");

    // Estados para modal de crear rango
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({
        tipo_id: "",
        min: "",
        max: "",
        porcentaje: "",
        fecha_inicio: new Date().toISOString().split("T")[0],
    });
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    const [createSuccess, setCreateSuccess] = useState(false);

    // Estados para tipos de comisión
    const [tiposComision, setTiposComision] = useState<TipoComision[]>([]);
    const [isLoadingTipos, setIsLoadingTipos] = useState(false);

    // Estados para vista de asignar tipo a distribuidores
    const [distribuidores, setDistribuidores] = useState<DistribuidorTipoAsignado[]>([]);
    const [isLoadingDistribuidores, setIsLoadingDistribuidores] = useState(false);
    const [errorDistribuidores, setErrorDistribuidores] = useState("");
    const [pageDistribuidores, setPageDistribuidores] = useState(0);
    const [rowsPerPageDistribuidores, setRowsPerPageDistribuidores] = useState(5);
    const [filterDistribuidores, setFilterDistribuidores] = useState("");

    // Estados para modal de asignar tipo
    const [openModalAsignar, setOpenModalAsignar] = useState(false);
    const [formDataAsignar, setFormDataAsignar] = useState({
        grupo_id: "",
        tipo_id: "",
        id_vendedor: "",
        fecha_inicio: new Date().toISOString().split("T")[0],
    });
    const [isAsignando, setIsAsignando] = useState(false);
    const [asignarError, setAsignarError] = useState("");
    const [asignarSuccess, setAsignarSuccess] = useState(false);

    // Estados para grupos de vendedores
    const [gruposVendedores, setGruposVendedores] = useState<GrupoVendedor[]>([]);
    const [isLoadingGrupos, setIsLoadingGrupos] = useState(false);

    // Estados para vendedores por cargo
    const [vendedoresPorCargo, setVendedoresPorCargo] = useState<any[]>([]);
    const [isLoadingVendedores, setIsLoadingVendedores] = useState(false);

    // Hooks
    const obtenerRangosMutation = useObtenerRangosComision();
    const crearRangoMutation = useCrearRangoComision();
    const obtenerTiposMutation = useObtenerTiposComision();
    const obtenerDistribuidoresMutation = useObtenerTiposAsignadosDistribuidores();
    const asignarTipoMutation = useAsignarTipoDistribuidor();
    const obtenerGruposMutation = useObtenerGruposCargosAsignados();
    const obtenerEmpleadosIdCargoMutation = useObtenerEmpleadosIdCargo();

    // Cargar rangos cuando se abre la vista de comisiones
    const fetchRangos = async () => {
        setIsLoadingRangos(true);
        setErrorRangos("");
        try {
            const response = await obtenerRangosMutation.mutateAsync();
            const data = (response as any)?.data || [];
            setRangos(data);
        } catch (err: any) {
            setErrorRangos(err?.message || "Error al cargar rangos de comisión");
        } finally {
            setIsLoadingRangos(false);
        }
    };

    useEffect(() => {
        if (activeView === "comisiones") {
            fetchRangos();
        }
    }, [activeView]);

    // Filtrar rangos
    const filteredRangos = rangos.filter(
        (rango) =>
            rango.tipo.toLowerCase().includes(filterText.toLowerCase()) ||
            rango.porcentaje.includes(filterText)
    );

    // Paginación
    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Cargar tipos de comisión
    const fetchTiposComision = async () => {
        setIsLoadingTipos(true);
        try {
            const response = await obtenerTiposMutation.mutateAsync();
            const data = (response as any)?.data || [];
            setTiposComision(data);
        } catch (err: any) {
            console.error("Error al cargar tipos de comisión:", err);
        } finally {
            setIsLoadingTipos(false);
        }
    };

    // Modal handlers
    const handleOpenModal = async () => {
        setOpenModal(true);
        setCreateError("");
        setCreateSuccess(false);
        setFormData({
            tipo_id: "",
            min: "",
            max: "",
            porcentaje: "",
            fecha_inicio: new Date().toISOString().split("T")[0],
        });
        // Cargar tipos si no están cargados
        if (tiposComision.length === 0) {
            await fetchTiposComision();
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleFormChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        setIsCreating(true);
        setCreateError("");
        setCreateSuccess(false);
        try {
            await crearRangoMutation.mutateAsync(formData);
            setCreateSuccess(true);
            // Recargar rangos
            await fetchRangos();
            // Cerrar modal después de 1 segundo
            setTimeout(() => {
                handleCloseModal();
            }, 1000);
        } catch (err: any) {
            setCreateError(err?.message || "Error al crear rango de comisión");
        } finally {
            setIsCreating(false);
        }
    };

    // === Funciones para Asignar Tipo a Distribuidores ===

    // Cargar distribuidores con tipos asignados
    const fetchDistribuidores = async () => {
        setIsLoadingDistribuidores(true);
        setErrorDistribuidores("");
        try {
            const response = await obtenerDistribuidoresMutation.mutateAsync();
            const data = (response as any)?.data || [];
            setDistribuidores(data);
        } catch (err: any) {
            setErrorDistribuidores(err?.message || "Error al cargar distribuidores");
        } finally {
            setIsLoadingDistribuidores(false);
        }
    };

    // Cargar grupos de vendedores
    const fetchGruposVendedores = async () => {
        setIsLoadingGrupos(true);
        try {
            const response = await obtenerGruposMutation.mutateAsync({});
            // La respuesta puede ser un array directo o tener .data
            if (Array.isArray(response)) {
                setGruposVendedores(response);
            } else if (response?.data && Array.isArray(response.data)) {
                setGruposVendedores(response.data);
            } else {
                setGruposVendedores([]);
            }
        } catch (err: any) {
            console.error("Error al cargar grupos:", err);
        } finally {
            setIsLoadingGrupos(false);
        }
    };

    // useEffect para cargar distribuidores
    useEffect(() => {
        if (activeView === "asignarTipo") {
            fetchDistribuidores();
        }
    }, [activeView]);

    // Filtrar distribuidores
    const filteredDistribuidores = distribuidores.filter(
        (dist) => {
            const nombreMostrar = dist.nombre_grupo || dist.nombre_emp || "";
            return (
                nombreMostrar.toLowerCase().includes(filterDistribuidores.toLowerCase()) ||
                dist.tipo.toLowerCase().includes(filterDistribuidores.toLowerCase())
            );
        }
    );

    // Paginación distribuidores
    const handleChangePageDistribuidores = (_event: unknown, newPage: number) => {
        setPageDistribuidores(newPage);
    };

    const handleChangeRowsPerPageDistribuidores = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPageDistribuidores(parseInt(event.target.value, 10));
        setPageDistribuidores(0);
    };

    // Modal handlers para asignar tipo
    const handleOpenModalAsignar = async () => {
        setOpenModalAsignar(true);
        setAsignarError("");
        setAsignarSuccess(false);
        setFormDataAsignar({
            grupo_id: "",
            tipo_id: "",
            id_vendedor: "",
            fecha_inicio: new Date().toISOString().split("T")[0],
        });
        setVendedoresPorCargo([]);
        // Cargar datos si no están cargados
        if (gruposVendedores.length === 0) {
            await fetchGruposVendedores();
        }
        if (tiposComision.length === 0) {
            await fetchTiposComision();
        }
    };

    const handleCloseModalAsignar = () => {
        setOpenModalAsignar(false);
        setVendedoresPorCargo([]);
    };

    const handleFormChangeAsignar = async (field: string, value: string) => {
        setFormDataAsignar((prev) => ({ ...prev, [field]: value }));

        // Si es el campo de grupo_id, verificar el nombre y hacer el POST si no empieza con DAS o Distribuidor
        if (field === "grupo_id" && value) {
            const grupoSeleccionado = gruposVendedores.find((g) => g.grupo_id === value);
            if (grupoSeleccionado) {
                const nombreGrupo = grupoSeleccionado.grupo;
                // Si el nombre NO empieza con "DAS" o "Distribuidor", hacer POST
                if (!nombreGrupo.startsWith("DAS") && !nombreGrupo.startsWith("Distribuidor")) {
                    try {
                        setIsLoadingVendedores(true);
                        const response = await obtenerEmpleadosIdCargoMutation.mutateAsync(grupoSeleccionado.cargos_ids);
                        const data = (response as any)?.data || [];
                        setVendedoresPorCargo(data);
                        // Limpiar id_vendedor cuando se selecciona un nuevo grupo
                        setFormDataAsignar((prev) => ({ ...prev, id_vendedor: "" }));
                    } catch (err: any) {
                        console.error("Error al obtener empleados por id del cargo:", err);
                        setVendedoresPorCargo([]);
                    } finally {
                        setIsLoadingVendedores(false);
                    }
                } else {
                    // Si es DAS o Distribuidor, limpiar la lista de vendedores
                    setVendedoresPorCargo([]);
                    setFormDataAsignar((prev) => ({ ...prev, id_vendedor: "" }));
                }
            }
        }
    };

    const handleSubmitAsignar = async () => {
        setIsAsignando(true);
        setAsignarError("");
        setAsignarSuccess(false);
        try {
            // Preparar datos a enviar
            const datosEnvio: any = {
                grupo_id: formDataAsignar.grupo_id,
                tipo_id: formDataAsignar.tipo_id,
                fecha_inicio: formDataAsignar.fecha_inicio,
            };
            
            // Agregar id_vendedor solo si está seleccionado
            if (formDataAsignar.id_vendedor) {
                datosEnvio.id_vendedor = formDataAsignar.id_vendedor;
            }
            
            await asignarTipoMutation.mutateAsync(datosEnvio);
            setAsignarSuccess(true);
            // Recargar distribuidores
            await fetchDistribuidores();
            // Cerrar modal después de 1 segundo
            setTimeout(() => {
                handleCloseModalAsignar();
            }, 1000);
        } catch (err: any) {
            setAsignarError(err?.message || "Error al asignar tipo a distribuidor");
        } finally {
            setIsAsignando(false);
        }
    };



    // Render de la vista de menú principal
    const renderMenu = () => (
        <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                        Panel de Administración
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Gestione la configuración de comisiones y grupos de vendedores
                    </Typography>
                </Box>
                <Divider />
            </Grid>

            {/* Sección de información */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        mt: 2,
                        p: 3,
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "grey.300",
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Información
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Este panel le permite administrar la configuración general del
                        módulo de comisiones. Los cambios realizados aquí afectarán los
                        cálculos de comisiones para todos los vendedores.
                    </Typography>
                </Box>
            </Grid>

            {/* Card: Configuración de Comisiones */}
            <Grid item xs={12} sm={6} md={4}>
                <Card
                    sx={{
                        height: "100%",
                        transition: "all 0.2s",
                        cursor: "pointer",
                        "&:hover": {
                            boxShadow: 4,
                            transform: "translateY(-2px)",
                        },
                    }}
                    onClick={() => setActiveView("comisiones")}
                >
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "success.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <TuneIcon sx={{ color: "success.main", fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Comisiones
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Administración de Rangos de Comisión.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="success"
                            fullWidth
                            startIcon={<TuneIcon />}
                            sx={{ textTransform: "none" }}
                        >
                            Configurar Comisiones
                        </Button>
                    </CardContent>
                </Card>
            </Grid>

            {/* Card: Asignar Tipo a Distribuidores */}
            <Grid item xs={12} sm={6} md={4}>
                <Card
                    sx={{
                        height: "100%",
                        transition: "all 0.2s",
                        cursor: "pointer",
                        "&:hover": {
                            boxShadow: 4,
                            transform: "translateY(-2px)",
                        },
                    }}
                    onClick={() => setActiveView("asignarTipo")}
                >
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "warning.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AssignmentIcon sx={{ color: "warning.main", fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Asignar Tipo a Grupos
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Administración de la asignación de tipos de comisión a grupos
                        </Typography>
                        <Button
                            variant="outlined"
                            color="warning"
                            fullWidth
                            startIcon={<AssignmentIcon />}
                            sx={{ textTransform: "none" }}
                        >
                            Asignar Tipos
                        </Button>
                    </CardContent>
                </Card>
            </Grid>

        </Grid>
    );

    // Render de la vista de comisiones
    const renderComisiones = () => (
        <Grid container spacing={3}>
            {/* Header con botón volver */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton
                            onClick={() => setActiveView("menu")}
                            sx={{
                                bgcolor: "grey.100",
                                "&:hover": { bgcolor: "grey.200" },
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 600, color: "text.primary" }}
                            >
                                Configuración de Rangos de Comisión
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                Administre los rangos y porcentajes de comisión por tipo de
                                distribuidor
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={handleOpenModal}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Nuevo rango
                    </Button>
                </Box>
                <Divider />
            </Grid>

            {/* Filtro */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Mostrar</Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(0);
                                }}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2">registros</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Filtro</Typography>
                        <TextField
                            size="small"
                            value={filterText}
                            onChange={(e) => {
                                setFilterText(e.target.value);
                                setPage(0);
                            }}
                            sx={{ width: 200 }}
                        />
                    </Box>
                </Box>
            </Grid>

            {/* Error */}
            {errorRangos && (
                <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setErrorRangos("")}>
                        {errorRangos}
                    </Alert>
                </Grid>
            )}

            {/* Tabla */}
            <Grid item xs={12}>
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "primary.main" }}>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Tipo Distribuidor
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Min
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Max
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    % Comisión
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Desde
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Hasta
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingRangos ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={32} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Cargando rangos...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredRangos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No se encontraron rangos de comisión
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRangos
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((rango) => (
                                        <TableRow
                                            key={rango.id}
                                            sx={{
                                                "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                                                "&:hover": { bgcolor: "action.hover" },
                                            }}
                                        >
                                            <TableCell>{rango.tipo}</TableCell>
                                            <TableCell>{rango.min}</TableCell>
                                            <TableCell>{rango.max}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${rango.porcentaje}%`}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{rango.fecha_inicio}</TableCell>
                                            <TableCell>
                                                {rango.fecha_fin ? (
                                                    rango.fecha_fin
                                                ) : (
                                                    <Chip
                                                        label="VIGENTE"
                                                        size="small"
                                                        color="primary"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Paginación */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Registros del {page * rowsPerPage + 1} al{" "}
                        {Math.min((page + 1) * rowsPerPage, filteredRangos.length)} de un
                        total de {filteredRangos.length}
                    </Typography>
                    <TablePagination
                        component="div"
                        count={filteredRangos.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[]}
                        labelDisplayedRows={() => ""}
                    />
                </Box>
            </Grid>

            {/* Modal para crear nuevo rango */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: "center", fontWeight: 600 }}>
                    CREAR RANGO COMISION
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Tipo Distribuidor */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Tipo Distribuidor:
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={formData.tipo_id}
                                    onChange={(e) => handleFormChange("tipo_id", e.target.value)}
                                    displayEmpty
                                    disabled={isLoadingTipos}
                                >
                                    <MenuItem value="" disabled>
                                        {isLoadingTipos ? "Cargando tipos..." : "- Seleccione un tipo -"}
                                    </MenuItem>
                                    {tiposComision.map((tipo) => (
                                        <MenuItem key={tipo.id} value={String(tipo.id)}>
                                            {tipo.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Rango mínimo y máximo */}
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Rango mínimo:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={formData.min}
                                onChange={(e) => handleFormChange("min", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Rango máximo:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={formData.max}
                                onChange={(e) => handleFormChange("max", e.target.value)}
                            />
                        </Grid>

                        {/* Porcentaje y Fecha inicio */}
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Porcentaje
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={formData.porcentaje}
                                onChange={(e) => handleFormChange("porcentaje", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Fecha inicio:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                value={formData.fecha_inicio}
                                onChange={(e) =>
                                    handleFormChange("fecha_inicio", e.target.value)
                                }
                            />
                        </Grid>

                        {/* Mensajes de error/éxito*/}
                        {createError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{createError}</Alert>
                            </Grid>
                        )}
                        {createSuccess && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Rango de comisión creado exitosamente
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseModal}
                        variant="outlined"
                        disabled={isCreating}
                    >
                        Cerrar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={
                            isCreating ||
                            !formData.tipo_id ||
                            !formData.min ||
                            !formData.max ||
                            !formData.porcentaje
                        }
                        startIcon={isCreating ? <CircularProgress size={16} /> : null}
                    >
                        {isCreating ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );

    // Render de la vista de Asignar Tipo a Distribuidores
    const renderAsignarTipo = () => (
        <Grid container spacing={3}>
            {/* Header con botón volver */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton
                            onClick={() => setActiveView("menu")}
                            sx={{
                                bgcolor: "grey.100",
                                "&:hover": { bgcolor: "grey.200" },
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 600, color: "text.primary" }}
                            >
                                Administración de Distribuidores
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                Asigne tipos de comisión a grupos o vendedores individuales
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={handleOpenModalAsignar}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Asignar Tipo
                    </Button>
                </Box>
                <Divider />
            </Grid>

            {/* Filtro */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Mostrar</Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={rowsPerPageDistribuidores}
                                onChange={(e) => {
                                    setRowsPerPageDistribuidores(Number(e.target.value));
                                    setPageDistribuidores(0);
                                }}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2">registros</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Filtro</Typography>
                        <TextField
                            size="small"
                            value={filterDistribuidores}
                            onChange={(e) => {
                                setFilterDistribuidores(e.target.value);
                                setPageDistribuidores(0);
                            }}
                            sx={{ width: 200 }}
                        />
                    </Box>
                </Box>
            </Grid>

            {/* Error */}
            {errorDistribuidores && (
                <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setErrorDistribuidores("")}>
                        {errorDistribuidores}
                    </Alert>
                </Grid>
            )}

            {/* Tabla */}
            <Grid item xs={12}>
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "primary.main" }}>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Grupo o Vendedor Individual
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Tipo
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Desde
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Hasta
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: 600 }}>
                                    Estado
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingDistribuidores ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={32} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Cargando distribuidores...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredDistribuidores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No se encontraron distribuidores con tipos asignados
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredDistribuidores
                                    .slice(
                                        pageDistribuidores * rowsPerPageDistribuidores,
                                        pageDistribuidores * rowsPerPageDistribuidores +
                                        rowsPerPageDistribuidores
                                    )
                                    .map((dist) => (
                                        <TableRow
                                            key={dist.id}
                                            sx={{
                                                "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                                                "&:hover": { bgcolor: "action.hover" },
                                            }}
                                        >
                                            <TableCell>{dist.nombre_grupo || dist.nombre_emp}</TableCell>
                                            <TableCell>{dist.tipo}</TableCell>
                                            <TableCell>{dist.fecha_inicio}</TableCell>
                                            <TableCell>
                                                {dist.fecha_fin ? (
                                                    dist.fecha_fin
                                                ) : (
                                                    <Chip
                                                        label="VIGENTE"
                                                        size="small"
                                                        color="primary"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={dist.fecha_fin ? "Inactivo" : "Activo"}
                                                    size="small"
                                                    color={
                                                        !dist.fecha_fin ? "success" : "default"
                                                    }
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Paginación */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Registros del {pageDistribuidores * rowsPerPageDistribuidores + 1} al{" "}
                        {Math.min(
                            (pageDistribuidores + 1) * rowsPerPageDistribuidores,
                            filteredDistribuidores.length
                        )}{" "}
                        de un total de {filteredDistribuidores.length}
                    </Typography>
                    <TablePagination
                        component="div"
                        count={filteredDistribuidores.length}
                        page={pageDistribuidores}
                        onPageChange={handleChangePageDistribuidores}
                        rowsPerPage={rowsPerPageDistribuidores}
                        onRowsPerPageChange={handleChangeRowsPerPageDistribuidores}
                        rowsPerPageOptions={[]}
                        labelDisplayedRows={() => ""}
                    />
                </Box>
            </Grid>

            {/* Modal para asignar tipo */}
            <Dialog
                open={openModalAsignar}
                onClose={handleCloseModalAsignar}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: "center", fontWeight: 600 }}>
                    ASIGNAR TIPO DISTRIBUIDOR
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Grupo */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Grupo:
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={formDataAsignar.grupo_id}
                                    onChange={(e) =>
                                        handleFormChangeAsignar("grupo_id", e.target.value)
                                    }
                                    displayEmpty
                                    disabled={isLoadingGrupos}
                                >
                                    <MenuItem value="" disabled>
                                        {isLoadingGrupos
                                            ? "Cargando grupos..."
                                            : "- Seleccione un grupo -"}
                                    </MenuItem>
                                    {gruposVendedores.map((grupo) => (
                                        <MenuItem key={grupo.grupo_id} value={grupo.grupo_id}>
                                            {grupo.grupo}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Vendedor (solo si hay vendedores disponibles) */}
                        {vendedoresPorCargo.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                    Vendedor:
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={formDataAsignar.id_vendedor}
                                        onChange={(e) =>
                                            handleFormChangeAsignar("id_vendedor", e.target.value)
                                        }
                                        displayEmpty
                                        disabled={isLoadingVendedores}
                                    >
                                        <MenuItem value="" disabled>
                                            {isLoadingVendedores
                                                ? "Cargando vendedores..."
                                                : "- Seleccione un vendedor -"}
                                        </MenuItem>
                                        {vendedoresPorCargo.map((vendedor: any) => (
                                            <MenuItem key={vendedor.id_emp} value={String(vendedor.id_emp)}>
                                                {vendedor.nombre_emp}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Tipo */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Tipo:
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={formDataAsignar.tipo_id}
                                    onChange={(e) =>
                                        handleFormChangeAsignar("tipo_id", e.target.value)
                                    }
                                    displayEmpty
                                    disabled={isLoadingTipos}
                                >
                                    <MenuItem value="" disabled>
                                        {isLoadingTipos
                                            ? "Cargando tipos..."
                                            : "- Seleccione un tipo -"}
                                    </MenuItem>
                                    {tiposComision.map((tipo) => (
                                        <MenuItem key={tipo.id} value={String(tipo.id)}>
                                            {tipo.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Fecha inicio */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Fecha inicio:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                value={formDataAsignar.fecha_inicio}
                                onChange={(e) =>
                                    handleFormChangeAsignar("fecha_inicio", e.target.value)
                                }
                            />
                        </Grid>

                        {/* Mensajes de error/éxito */}
                        {asignarError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{asignarError}</Alert>
                            </Grid>
                        )}
                        {asignarSuccess && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Tipo asignado exitosamente al distribuidor
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseModalAsignar}
                        variant="outlined"
                        disabled={isAsignando}
                    >
                        Cerrar
                    </Button>
                    <Button
                        onClick={handleSubmitAsignar}
                        variant="contained"
                        color="primary"
                        disabled={
                            isAsignando ||
                            !formDataAsignar.grupo_id ||
                            !formDataAsignar.tipo_id
                        }
                        startIcon={isAsignando ? <CircularProgress size={16} /> : null}
                    >
                        {isAsignando ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );

    // Render de vistas placeholder
    const renderPlaceholder = (title: string, description: string) => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <IconButton
                        onClick={() => setActiveView("menu")}
                        sx={{
                            bgcolor: "grey.100",
                            "&:hover": { bgcolor: "grey.200" },
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            {description}
                        </Typography>
                    </Box>
                </Box>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "grey.300",
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        Próximamente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Esta sección está en desarrollo
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );

    // Render principal
    return (
        <>
            {activeView === "menu" && renderMenu()}
            {activeView === "comisiones" && renderComisiones()}
            {activeView === "grupos" &&
                renderPlaceholder(
                    "Grupos de Vendedores",
                    "Administre los grupos de vendedores"
                )}
            {activeView === "asignarTipo" && renderAsignarTipo()}
        </>
    );
}
